import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import * as linkify from 'linkifyjs';

const mentionStyle = {
  backgroundColor: 'transparent',
  borderBottom: 'none',
  color: 'inherit',
  fontWeight: 700,
  textDecoration: 'none'
};

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (React.isValidElement(node)) return extractText(node.props.children);

  return '';
}

function mentionNamesFrom(mentionSearchWords) {
  return [
    ...new Set(
      mentionSearchWords
        .map((word) =>
          String(word || '')
            .replace(/^@/, '')
            .trim()
        )
        .filter(Boolean)
    )
  ].sort((a, b) => b.length - a.length);
}

function unwrapMentionMarkup(text) {
  return text.replace(/@\[([^\]]+)\]\(([^)]+)\)/g, '@$1');
}

function isMentionLink(href, text, names) {
  if (!names.length) return false;

  const normalizedText = String(text || '')
    .replace(/^@/, '')
    .trim()
    .toLowerCase();
  const namesLower = names.map((name) => name.toLowerCase());
  const textIsMention = normalizedText && namesLower.includes(normalizedText);
  const isWebUrl = /^(https?:|\/|#)/i.test(href || '');

  if (textIsMention && !isWebUrl) return true;

  if (href && /^mailto:/i.test(href)) {
    const email = href
      .replace(/^mailto:/i, '')
      .trim()
      .toLowerCase();

    return namesLower.includes(email);
  }

  return false;
}

function linkifyText(text, keyPrefix) {
  if (!text) return text;

  let matches = [];
  try {
    matches = linkify.find(text) || [];
  } catch (error) {
    return text;
  }

  if (!matches.length) return text;

  const parts = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    const start = text.indexOf(match.value, cursor);
    if (start < 0) return;

    if (start > cursor) parts.push(text.slice(cursor, start));

    parts.push(
      <a
        key={`${keyPrefix}-link-${index}`}
        href={match.href}
        rel="noopener noreferrer"
        target="_blank"
      >
        {match.value}
      </a>
    );

    cursor = start + match.value.length;
  });

  if (cursor < text.length) parts.push(text.slice(cursor));

  return parts;
}

function highlightText(text, names, keyPrefix) {
  if (!text) return text;
  if (!names.length) return linkifyText(text, keyPrefix);

  const tokens = names.flatMap((name) => [`@${name}`, name]);
  const pattern = new RegExp(`(${tokens.map(escapeRegExp).join('|')})`, 'g');
  const namesLower = new Set(tokens.map((token) => token.toLowerCase()));

  return text.split(pattern).map((part, index) => {
    if (!part) return part;

    if (!namesLower.has(part.toLowerCase())) {
      return linkifyText(part, `${keyPrefix}-${index}`);
    }

    return (
      <strong key={`${keyPrefix}-mention-${index}`} style={mentionStyle}>
        {part}
      </strong>
    );
  });
}

function isOpaqueToTextProcessing(type) {
  if (type === 'a' || type === 'code' || type === 'pre') return true;

  return typeof type === 'function' && type.opaqueToTextProcessing === true;
}

function highlightMentions(children, names) {
  return React.Children.map(children, (child, childIndex) => {
    if (typeof child === 'string' || typeof child === 'number') {
      return highlightText(String(child), names, `text-${childIndex}`);
    }

    if (!React.isValidElement(child)) return child;

    if (isOpaqueToTextProcessing(child.type)) return child;

    if (child.props && child.props.children != null) {
      return React.cloneElement(child, {
        children: highlightMentions(child.props.children, names)
      });
    }

    return child;
  });
}

function createMarkdownComponents(names) {
  const withMentions =
    (Tag, style) =>
    ({ children, node, ...props }) =>
      (
        <Tag style={style} {...props}>
          {highlightMentions(children, names)}
        </Tag>
      );

  const Anchor = ({ children, href, node, ...props }) => {
    const text = extractText(children);
    if (isMentionLink(href, text, names)) {
      return <strong style={mentionStyle}>{text}</strong>;
    }

    return (
      <a href={href} rel="noopener noreferrer" target="_blank" {...props}>
        {children}
      </a>
    );
  };
  Anchor.opaqueToTextProcessing = true;

  return {
    p: withMentions('p', { margin: '0 0 8px 0' }),
    h1: withMentions('h1', { margin: '8px 0 4px', fontSize: '18px', fontWeight: 700 }),
    h2: withMentions('h2', { margin: '8px 0 4px', fontSize: '17px', fontWeight: 700 }),
    h3: withMentions('h3', { margin: '8px 0 4px', fontSize: '16px', fontWeight: 700 }),
    h4: withMentions('h4', { margin: '8px 0 4px', fontSize: '15px', fontWeight: 700 }),
    h5: withMentions('h5', { margin: '8px 0 4px', fontSize: '14px', fontWeight: 700 }),
    h6: withMentions('h6', { margin: '8px 0 4px', fontSize: '13px', fontWeight: 700 }),
    ul: ({ children, node, ordered, depth, ...props }) => (
      <ul style={{ margin: '0 0 8px 0', paddingLeft: 20 }} {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, node, ordered, depth, ...props }) => (
      <ol style={{ margin: '0 0 8px 0', paddingLeft: 20 }} {...props}>
        {children}
      </ol>
    ),
    li: ({ children, node, ordered, index, checked, ...props }) => (
      <li style={{ marginBottom: 4 }} {...props}>
        {highlightMentions(children, names)}
      </li>
    ),
    a: Anchor,
    strong: ({ children, node, ...props }) => <strong {...props}>{children}</strong>,
    em: ({ children, node, ...props }) => <em {...props}>{children}</em>
  };
}

export default function NoteMarkdownContent({ text, mentionSearchWords = [], className, style }) {
  if (!text) return null;

  const names = mentionNamesFrom(mentionSearchWords);

  return (
    <div className={className} style={{ wordBreak: 'break-word', ...style }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={createMarkdownComponents(names)}
      >
        {unwrapMentionMarkup(text)}
      </ReactMarkdown>
    </div>
  );
}
